import React from "react";
import { Box, styled } from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { fontSize, gaps, padding, borderRadius } from "theme/defaultStyle";
import { PolicyManagementRecord } from "entities/UIModel/PolicyManagementRecord";
import ActionButton from "controls/global/action-button/ActionButton";
import LockOpenIcon from "@mui/icons-material/LockOpen";

// Hex literals (not in theme/defaultStyle's palette) matching the approved
// mockup — purple, distinct from the amber CqBanner / blue HistCleanBanner.
const Banner = styled(Box)({
  display: "flex",
  alignItems: "center",
  gap: gaps.small1,
  marginTop: gaps.small1,
  marginBottom: gaps.small1,
  backgroundColor: "#f3e8fd",
  border: "1px solid #c4a6f0",
  borderRadius: borderRadius.small,
  padding: `${padding.small1} ${padding.medium1}`,
  color: "#4c1d95",
});

export const relativeTime = (isoDate?: string | null): string | null => {
  if (!isoDate) return null;
  const then = new Date(isoDate).getTime();
  if (isNaN(then)) return null;
  const minutes = Math.max(0, Math.round((Date.now() - then) / 60000));
  if (minutes < 1) return "just now";
  if (minutes === 1) return "1 min ago";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return hours === 1 ? "1 hour ago" : `${hours} hours ago`;
  const days = Math.round(hours / 24);
  return days === 1 ? "1 day ago" : `${days} days ago`;
};

const minutesUntil = (isoDate?: string | null): number | null => {
  if (!isoDate) return null;
  const until = new Date(isoDate).getTime();
  if (isNaN(until)) return null;
  const minutes = Math.round((until - Date.now()) / 60000);
  return minutes > 0 ? minutes : null;
};

interface LockedBannerProps {
  record: PolicyManagementRecord;
  // From the acquire response, so the holder is named even when the grid row is
  // stale. Falls back to the row's own lock columns.
  lockedByUser?: string | null;
  lockedUntil?: string | null;
  // "beforeErrors": short message shown above Policy Identity when the record
  // has open errors and is locked by someone else. "default" is the original
  // wording, shown at the bottom of the drawer.
  variant?: "default" | "beforeErrors";
  // Stub until a force-release endpoint exists (ReleasePolicyTransactionLock
  // only releases the caller's own lock).
  onUnlockClick?: () => void;
}

// Shown to every user other than the one holding the edit lock. Reused as-is
// across the queue drawers (PolicyDetailsPanel) and Product Details.
const LockedBanner = ({
  record,
  lockedByUser,
  lockedUntil,
  variant = "default",
  onUnlockClick,
}: LockedBannerProps) => {
  const holder = lockedByUser || record.lockedBy || "another user";
  const lastActivity = relativeTime(record.lockedDate);
  const freesUpIn = minutesUntil(lockedUntil);

  if (variant === "beforeErrors") {
    return (
      <Banner>
        <LockIcon sx={{ fontSize: 20 }} />
        <Box component="span" sx={{ flex: 1, fontSize: fontSize.small }}>
          <strong>Locked by {holder}.</strong> This record must be unlocked
          before fixing errors.
          {lastActivity ? ` Last update: ${lastActivity}` : ""}
        </Box>
      </Banner>
    );
  }

  return (
    <Banner>
      <LockIcon sx={{ fontSize: 20 }} />
      <Box component="span" sx={{ flex: 1, fontSize: fontSize.small }}>
        <strong>Record locked.</strong> Currently in use by{" "}
        <strong>{holder}</strong>
        {lastActivity ? ` (last activity ${lastActivity})` : ""}. Actions are
        unavailable until the editing session ends
        {freesUpIn ? `, or in ${freesUpIn} min when the lock expires` : ""}.
      </Box>
      {onUnlockClick && (
        <ActionButton
          variant="outlined"
          color="secondary"
          startIcon={<LockOpenIcon />}
          onClick={onUnlockClick}
        >
          Unlock Record
        </ActionButton>
      )}
    </Banner>
  );
};

export default LockedBanner;
